import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import useScroll from "hooks/use-scroll.hook";
import {
  useGetAmbientScribeProcessingContextQuery,
  useGetProcessingEventsQuery
} from "services/ambient-scribe";
import type { AmbientScribeProcessingContext } from "services/types/ambient-scribe-processing-context.type";
import type { ProcessingEvent } from "services/types/processing-event.type";

import AmbientScribeDetailsAccordion from "./ambient-scribe-details-accordion.component";
import ProcessingEventTableRow from "./processing-event-table-row.component";

const EVENTS_PAGE_LIMIT = 25;

type ListState = {
  events: ProcessingEvent[];
  hasMore: boolean;
};

type PagePayload = {
  pageOffset: number;
  page: ProcessingEvent[];
  next: string | null;
  limit: number;
};

const initialListState: ListState = { events: [], hasMore: true };

function mergeProcessingEventsPage(
  state: ListState,
  payload: PagePayload
): ListState {
  const { pageOffset, page, next, limit } = payload;
  let hasMore = Boolean(next);
  if (page.length < limit) {
    hasMore = false;
  }

  if (pageOffset === 0) {
    return { events: page, hasMore };
  }

  const ids = new Set(state.events.map((e) => e.id));
  const uniqueNew = page.filter((e) => !ids.has(e.id));
  if (uniqueNew.length === 0) {
    return { ...state, hasMore: false };
  }

  return {
    events: [...state.events, ...uniqueNew],
    hasMore
  };
}

function processingContextToAccordionData(
  context: AmbientScribeProcessingContext
): Record<string, unknown> {
  return JSON.parse(JSON.stringify(context)) as Record<string, unknown>;
}

interface ProcessingEventsTimelineProps {
  scribeId: number | string | undefined;
}

export default function ProcessingEventsTimeline(
  props: Readonly<ProcessingEventsTimelineProps>
) {
  const { scribeId } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [pageOffset, setPageOffset] = useState(0);
  const [listState, dispatchList] = useReducer(
    (
      s: ListState,
      a: { type: "reset" } | { type: "page"; payload: PagePayload }
    ) => {
      if (a.type === "reset") {
        return initialListState;
      }
      return mergeProcessingEventsPage(s, a.payload);
    },
    initialListState
  );

  const numericScribeId = Number(scribeId);
  const skip = !scribeId;

  useEffect(() => {
    setPageOffset(0);
    dispatchList({ type: "reset" });
  }, [scribeId]);

  const {
    data: processingContext,
    isLoading: isContextLoading,
    isError: isContextError,
    refetch: refetchProcessingContext
  } = useGetAmbientScribeProcessingContextQuery(
    { id: numericScribeId },
    {
      skip,
      refetchOnMountOrArgChange: true
    }
  );

  const {
    data,
    isLoading,
    isFetching,
    isError,
    isSuccess,
    isUninitialized,
    refetch
  } = useGetProcessingEventsQuery(
    {
      scribeId: numericScribeId,
      offset: pageOffset,
      limit: EVENTS_PAGE_LIMIT
    },
    {
      skip,
      refetchOnMountOrArgChange: true
    }
  );

  useEffect(() => {
    if (skip || isFetching || isError || !data || !isSuccess) return;
    dispatchList({
      type: "page",
      payload: {
        pageOffset,
        page: data.results ?? [],
        next: data.next,
        limit: EVENTS_PAGE_LIMIT
      }
    });
  }, [data, isFetching, isError, isSuccess, pageOffset, skip]);

  const fetchMore = useCallback(() => {
    if (!listState.hasMore || isFetching) return;
    setPageOffset((o) => o + EVENTS_PAGE_LIMIT);
  }, [listState.hasMore, isFetching]);

  const { events, hasMore: hasMoreData } = listState;

  const hasBeenInitiallyLoaded = !skip && !isLoading && !isUninitialized;

  const ambientScribeAccordion = useMemo(
    () =>
      processingContext
        ? processingContextToAccordionData(processingContext)
        : undefined,
    [processingContext]
  );

  const triggerRef = useScroll({
    containerRef,
    isLoading: isFetching,
    hasMoreData: !isError && hasMoreData,
    fetchMore
  });

  const showEmptyState =
    hasBeenInitiallyLoaded && !isLoading && events.length === 0 && !isError;

  const showLoadingMore = isFetching && hasBeenInitiallyLoaded && pageOffset > 0;

  const showContextLoading = skip ? false : isContextLoading && !processingContext;

  return (
    <Stack spacing={0} sx={{ mt: 1 }}>
      {isContextError && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => refetchProcessingContext()}
            >
              Retry
            </Button>
          }
        >
          Unable to load scribe processing context. Check your connection and try
          again.
        </Alert>
      )}

      {showContextLoading && (
        <Stack alignItems="center" py={2} sx={{ mb: 1 }}>
          <CircularProgress size={24} />
        </Stack>
      )}

      {ambientScribeAccordion && (
        <AmbientScribeDetailsAccordion data={ambientScribeAccordion} />
      )}

      {isError && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Unable to load processing events. Check your connection and try again.
        </Alert>
      )}

      {showEmptyState && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No processing events recorded.
        </Typography>
      )}

      {(events.length > 0 || (isLoading && !hasBeenInitiallyLoaded)) && (
        <Box
          ref={containerRef}
          sx={{
            maxHeight: "min(60vh, 480px)",
            overflow: "auto",
            borderRadius: 1
          }}
        >
          <TableContainer sx={{ overflow: "unset" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" aria-hidden sx={{ width: 56 }} />
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Operation</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((event) => (
                  <ProcessingEventTableRow key={event.id} event={event} />
                ))}
              </TableBody>
            </Table>
            {isLoading && !hasBeenInitiallyLoaded && (
              <Stack alignItems="center" py={2}>
                <CircularProgress size={24} />
              </Stack>
            )}
            {showLoadingMore && (
              <Stack alignItems="center" py={1}>
                <CircularProgress size={20} />
              </Stack>
            )}
            <Box
              ref={triggerRef}
              sx={{
                display:
                  isFetching || isError || !hasMoreData ? "none" : "block",
                height: 16
              }}
            />
          </TableContainer>
        </Box>
      )}
    </Stack>
  );
}
